import React, { useEffect, useState } from "react";
import classNames from "classnames";

import { useStateSelector } from "utils/field";

import InfoTooltip from "components/InfoTooltip";
import WarningTooltip from "components/WarningTooltip";
import NonH2kParamTooltip from "components/NonH2kParamTooltip";
import LoadingDots from "components/LoadingDots";
import LockTooltip from "components/LockTooltip";

import classes from "./style.module.scss";

export const isZeroLikeValue = (val) => {
    if (val === "" || val == null) return false;

    const normalized = String(val).trim().replace(/^=/, "");
    if (normalized === "") return false;

    const num = parseFloat(normalized);
    return !isNaN(num) && num === 0;
};

export const shouldClearZeroOnFocus = (value, displayValues = []) => {
    if (value !== 0 && value !== "0" && parseFloat(value) !== 0) return false;

    return displayValues.some(isZeroLikeValue);
};

export const blurOnEnter = (event) => {
    if (event.key === "Enter") {
        event.preventDefault();
        event.currentTarget.blur();
        return true;
    }
    return false;
};

export const createInputFocusHandler = ({ value, displayValues, onClear, onFocus }) => (event) => {
    onFocus?.(event);

    if (shouldClearZeroOnFocus(value, displayValues)) {
        onClear();
    }
};

export const createInputKeyDownHandler = ({ onKeyDown, onFocus, focused }) => (event) => {
    if (blurOnEnter(event)) return;
    
    blurOnEnter(event);

    if (focused !== undefined && !focused) {
        onFocus?.();
    }

    onKeyDown?.(event);
};

const Input = ({
    name,
    label,
    input = {},
    meta = {},
    type,
    className,
    large,
    compact = false,
    highlight = false,
    isStat = false,
    transparent = false,
    placeholder = "",
    hideLabel,
    autoComplete = "off",
    info = "",
    warning = "",
    lockNotice = "",
    nonH2kParam = "",
    disabled = false,
    parentError = false,
    quantity = false,
    setValue,
    setTouched = false,
    decimals,
    isLoading = false,
    icon,
    leftIcon,
    style,
    onBlurAction = () => {},
    reset = false,
    actionKey,
    onKeyAction = () => {},
    withLimit = false,
    limit = 0,
    labelStyle = {},
    tabIndex = 0,
    isNewDesign = false,
}) => {
    const [touched, toggleTouched] = useState(false);
    const [isFocused, setIsFocused] = useState(false);
    const { invalid = false, error = "" } = meta;
    const { value, onChange, onFocus: inputOnFocus, onKeyDown: inputOnKeyDown, ...inputRest } = input;
    const isTouched = touched || setValue || setTouched;
    const isTextOrNumber = type === "text" || type === "number";
    const newDesignLabelFloating = isFocused || (value !== "" && value != null && value !== undefined);

    const classNamesList = [
        classes.inputField,
        className && className,
        large && classes.large,
        compact && classes.compact,
        highlight && !disabled && classes.highlight,
        isStat && classes.smallStat,
        transparent && classes.transparent,
        quantity && classes.hasQuantity,
        (parentError || (invalid && isTouched)) && classes.invalid,
        leftIcon && classes.hasLeftIcon,
    ].filter((c) => c);

    const countUp = () => onChange(parseFloat(value || 0) + 1);
    const countDown = () => {
        if (parseFloat(value) - 1 >= 0) {
            onChange(parseFloat(value || 0) - 1);
        }
    };

    const handleChange = (e) => {
        // Parse if number
        const name = e.target.name;
        var newValue = type === "number" ? parseFloat(e.target.value) : e.target.value;
        // Avoid setting field to NaN when e.target.value === ""
        newValue = type === "number" && isNaN(newValue) ? null : newValue;
        onChange(newValue, name);
    };

    const handleBlur = (e) => {
        onBlurAction(e);

        toggleTouched(true);
        // Number decimal places
        if (type === "number" && !isNaN(decimals)) {
            const decimalVal = parseFloat(parseFloat(value).toFixed(decimals));
            onChange(decimalVal);
        }

        // TODO: Check if this is needed
        // If field is exited and is empty, default to 0
        // if (type === "number" && isNaN(parseFloat(e.target.value))) {
        //     onChange(0);
        // }
    };

    const checkValue = useStateSelector(name);

    const handleFocus = createInputFocusHandler({
        value,
        displayValues: [value],
        onClear: () => onChange(type === "number" ? null : ""),
        onFocus: (event) => {
            if (isNewDesign) setIsFocused(true);
            if (isTextOrNumber) inputOnFocus?.(event);
        },
    });

    const handleKeyDown = (actionKey && onKeyAction) ? (event) => {
        if (actionKey && onKeyAction && event.key === actionKey) {
            onKeyAction(event);
        }
    } : createInputKeyDownHandler({
        onKeyDown: (event) => {
            inputOnKeyDown?.(event);
        },
    });

    const handleInputBlur = (e) => {
        if (isNewDesign) setIsFocused(false);
        handleBlur(e);
    };

    // TODO: Check if this is needed
    useEffect(() => {
        // Convert to decimal places
        if (setValue && type === "number" && !isNaN(decimals)) {
            return onChange(parseFloat(setValue.toFixed(decimals)));
        }
        if (setValue && type === "number") {
            return onChange(parseFloat(setValue));
        }
        if ((setValue || setValue === 0) && checkValue !== null) {
            onChange(setValue);
        }
    }, [setValue]);
    
    useEffect(() => {
        if (reset) {
            onChange(null);
        }
    }, [reset]);

    return (
        <div
            className={classNamesList.join(" ")}
            style={{ ...style, ...(isNewDesign ? { position: "relative" } : {}) }}
        >
            <div className={classes.fieldHeader}>
                <label style={labelStyle} className={classNames({ ["invisible"]: hideLabel })} htmlFor={name}>
                    {label}
                </label>
                {lockNotice && !isStat && <LockTooltip className={classes.lockTooltip} lockNotice={lockNotice} name={name}/>}
                {info && !isStat && <InfoTooltip className={classes.infoTooltip} info={info} />}
                {warning && !isStat && (
                    <WarningTooltip
                        className={classNames(classes.warningTooltip, { [classes.pushLeft]: info || lockNotice })}
                        warning={warning}
                    />
                )}
                {nonH2kParam && !isStat && (
                    <NonH2kParamTooltip
                        className={classNames(classes.nonH2kParamTooltip, { [classes.pushLeft]: warning })}
                        nonH2kParam={nonH2kParam}
                    />
                )}
            </div>
            {isLoading && (
                <div className={classes.loading}>
                    <LoadingDots />
                </div>
            )}
            {leftIcon && <img className={classes.leftInputIcon} src={leftIcon} alt={leftIcon} />}
            {!isLoading &&
                (isNewDesign ? (
                    <div className={classes.newDesignInputWrap}>
                        <span
                            className={classNames(classes.newDesignLabel, {
                                [classes.activeNewDesignLabel]: newDesignLabelFloating,
                            })}
                        >
                            {label}
                        </span>
                        <input
                            {...inputRest}
                            id={name}
                            name={name}
                            type={type}
                            placeholder=""
                            autoComplete={autoComplete}
                            disabled={disabled}
                            value={value}
                            onChange={handleChange}
                            onBlur={handleInputBlur}
                            onFocus={handleFocus}
                            onKeyDown={isTextOrNumber ? handleKeyDown : inputOnKeyDown}
                            tabIndex={tabIndex}
                        />
                    </div>
                ) : (
                    <input
                        {...inputRest}
                        id={name}
                        name={name}
                        type={type}
                        placeholder={placeholder}
                        autoComplete={autoComplete}
                        disabled={disabled}
                        value={value}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        onFocus={isTextOrNumber ? handleFocus : inputOnFocus}
                        onKeyDown={isTextOrNumber ? handleKeyDown : inputOnKeyDown}
                        tabIndex={tabIndex}
                    />
                ))}
            {icon && <img className={classes.inputIcon} src={icon} alt={icon} />}
            {quantity && !isLoading && (
                <div className={classes.quantity}>
                    <div onClick={countUp}>
                        <span></span>
                        <span></span>
                    </div>
                    <div onClick={countDown}>
                        <span></span>
                    </div>
                </div>
            )}

            {invalid && !isStat && isTouched && error && <span className={classes.errorMessage}>{error}</span>}
            {withLimit && (
                <span className={classes.limitMessage}>
                    {value.length}/{limit}
                </span>
            )}
        </div>
    );
};

export default Input;
